<template>
  <div class="mapbox-wrapper">
    <div id="map">
      <div id="current-position">
        <div id="middle-point"></div>
        <div id="circle-1"></div>
        <div id="circle-2"></div>
      </div>
      <waypoint v-if="currentWaypoint.id" :wayPoint="currentWaypoint"></waypoint>
    </div>
  </div>
  <end></end>
</template>

<script>
// @ is an alias to /src
import mapboxgl from "mapbox-gl"; // or "const mapboxgl = require('mapbox-gl');"
import contentfulClient from "@/module/contentful";
import getCoordinatesFromGpxFile from "@/module/gpx-utilities";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import * as turf from "@turf/helpers";
import bearing from "@turf/bearing";
import bezierSpline from "@turf/bezier-spline";
import distance from "@turf/distance";
import Waypoint from '@/components/Waypoint.vue';
import End from "@/components/End";


export default {
  name: "Mapbox",
  components: {End, Waypoint},
  data(){
    return{
      currentWaypoint: {}
    }
  },
  mounted: async function() {
    // Load Data from Contenful
    let wayPoints = await getWayPoints();
    wayPoints = wayPoints.reverse();
    const aareCoordinates = await getAareCoordinates();
    console.log("mounted:function -> aareCoordinates.length", aareCoordinates.length);
    
    // Calculate Nearest Coordinates on Path for specified Locations
    wayPoints.forEach(wayPoint => writeNearestAareIndex(wayPoint))
  
    // Save Numbers into Waypoints
    wayPoints.sort((a, b) => a.nearestAareIndex - b.nearestAareIndex);
    wayPoints.forEach((wayPoint,index) => wayPoint.number = index + 1)

    // Activate first wayPoint right away and hide its marker
    this.currentWaypoint = wayPoints[0];
    wayPoints[0].markerInvisible = true
  
    // Store this for function calls
    const self = this;
    

    // Initialize map and add route
    const map = initializeMapbox();
    map.on("load", function() {
      addEmptyRoute();
    });

    // Configure GSAP Animation with ScrollTrigger
    const timeline = configureAnimation();

    //------------Helper Functions-------------

    async function getWayPoints() {
      let result = await contentfulClient.getEntries({
        content_type: "wayPoint",
      });
      return result.items.map((item) => item.fields);
    }

    function getCoord(wayPoint) {
      return [wayPoint.location.lon, wayPoint.location.lat];
    }

    async function getAareCoordinates() {
      const steps = 5000

      //get gpx file from contentful
      const result = await contentfulClient.getEntry("24P6nJwW5DQbzOiEdPNawu");
      const gpxTrackFile = result.fields.gpxTrack.fields.file.url;
      const gpxCoordinates = await getCoordinatesFromGpxFile(gpxTrackFile);

      const curved = bezierSpline(turf.lineString(gpxCoordinates), {
        resolution: steps * 20,
      });
      return curved.geometry.coordinates.reverse();
    }

    function writeNearestAareIndex(wayPoint) {
      const from = getCoord(wayPoint);
      let min = 1000;
      let minIndex = -1;
      aareCoordinates.forEach((to, index) => {
        const dist = distance(from, to);
        if (dist < min) {
          min = dist;
          minIndex = index;
        }
      });
      wayPoint.nearestAareIndex = minIndex;
    }

    // Funktion um Punkt auf Route anzusteuern
    // t: Wert zwischen 0 und 1
    function setProgress(t) {
      const index = Math.round(t * aareCoordinates.length);

      const p1 = turf.point(aareCoordinates[index]);
      const p2 = turf.point(aareCoordinates[index + 1]);
      const calculatedBearing = bearing(p1, p2);

      map.jumpTo({
        center: aareCoordinates[index],
        bearing: calculatedBearing,
      });

      // Route updaten
      const slicedAareCoordinates = aareCoordinates.slice(0, Math.max(2,index)); // at least two elements two preserve Errors
      const featureCollection = turf.lineString(slicedAareCoordinates);
      map.getSource("route").setData(featureCollection);

      // Markers updaten
      wayPoints.forEach(wayPoint => {
        if(index > wayPoint.nearestAareIndex){
          addMarker(wayPoint)
        }else{
          removeMarker(wayPoint)
        }
      })
    }

    function addMarker(wayPoint) {
      
      if (!wayPoint.marker) {
        console.log("Added wayPoint", wayPoint)
        self.currentWaypoint = wayPoint;
        
        var el = document.createElement("div");
        el.className = "marker";

        const coord = aareCoordinates[wayPoint.nearestAareIndex];
        wayPoint.marker = new mapboxgl.Marker(el);

        if(!wayPoint.markerInvisible) wayPoint.marker.setLngLat(coord).addTo(map);
      }
    }

    function removeMarker(wayPoint) {
      // console.log("removeMarker -> wayPoint", wayPoint)
      if(wayPoint.marker){
        console.log("Removed wayPoint", wayPoint)
        self.currentWaypoint = wayPoint;

        wayPoint.marker.remove();
        wayPoint.marker = undefined;
      }
    }

    function configureAnimation() {
      gsap.registerPlugin(ScrollTrigger);
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: "#map",
          start: "center center",
          end: function (){ return '+='+window.innerHeight*6 + ' bottom'},
          toggleActions: "restart pause reverse pause",
          markers: false,
          scrub: 1,
          pin: true,
        },
      });
      tl.set("#map", { opacity: 1 });
      tl.to("#map", { duration: 5, rotateX: "0deg" });
      tl.to('.end',{duration:1})
      tl.to("#map", {
        duration: 4,
        ease: "steps(200)",
        width: "110vw",
        maxWidth: "110vw",
        height: "110vh",
        maxHeight: "110vh",
        y: "-20vh",
        x: "-25vw",
        onUpdate: function() {
          map.resize();
        },
      });
      const progressState = { value: 0.0 };
      tl.to(progressState, {
        duration: 25,
        value: 1.0,
        ease: "steps(" + aareCoordinates.length + ")",
        onUpdate: function() {
          setProgress(progressState.value);
        },
      });

      tl.set(".end", { display: "block" });
      tl.to('.end',{duration:1})
      tl.to("#map", {
        duration: 4,
        ease: "steps(200)",
        width: "50vw",
        maxWidth: "50vw",
        height: "60vh",
        maxHeight: "60vh",
        y: "2.5vw",
        x: "5vw",
        onUpdate: function() {
          map.resize();
        },
      });
      tl.to('.end',{duration:1})
      tl.to('.end', {
        opacity: 1,
        duration: 1,
      })

      tl.to('.end',{duration:2})
      return tl
    }

    function initializeMapbox() {
      mapboxgl.accessToken = "pk.eyJ1Ijoic3R1bWkiLCJhIjoiY2toZDc4NmdxMDMyeDJ5bnZjN2ZjeG9tdSJ9.bWxFyuE3aq-ubzj3cfckiQ";
      const map = new mapboxgl.Map({
        container: "map",
        style: "mapbox://styles/stumi/ckhen4ps80hpw19pdqkz4zcda", // stylesheet location
        center: aareCoordinates[0],
        bearing: -63.85,
        zoom: 16, // starting zoom
        attributionControl: false,
        interactive: false,
      });
      return map;
    }

    function addEmptyRoute() {
      map.addSource("route", {
        type: "geojson",
        data: turf.lineString([
          [0, 0],
          [0, 0],
        ]),
      });

      map.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#f05a2f",
          "line-width": 3,
        },
      });
    }
  },
};
</script>
<style lang="scss">
@import url("https://api.mapbox.com/mapbox-gl-js/v1.12.0/mapbox-gl.css");

#previousContent {
  height: 10vh;
  line-height: 10vh;
  background-color: aquamarine;
}

.mapbox-wrapper {
  position: relative;
  margin-top: -75vh;
  height: auto;
  #map {
    z-index: 8;
    height: 70vh;
    width: 60vw;
    position: absolute;
    top: 15vh;
    left: 20vw;

    transform: perspective(800px) rotateX(90deg);

    border: 12px solid #f7f7eb;
    border-radius: 30px;
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));

    opacity: 0;
  }

  #current-position {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    width: 10vw;
    height: 10vw;

    z-index: 10;

    #middle-point {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);

      width: 1vw;
      height: 1vw;

      background-color: #f05a2f;
      border-radius: 100%;

      z-index: 10;
    }

    #circle-1 {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);

      width: 2.5vw;
      height: 2.5vw;

      border: 3px solid #f05a2f;
      border-radius: 100%;
    }

    #circle-2 {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);

      width: 4vw;
      height: 4vw;

      border: 3px solid #f05a2f;
      border-radius: 100%;
    }
  }
}

.marker {
  width: 1vw;
  height: 1vw;
  border-radius: 100%;
  background-color: #f05a2f;
}

/* Not Legal!  */
.mapboxgl-ctrl-logo {
  display: none !important;
}

</style>
